// v3.18-1393 CRM-INT-008 Phase A — Enterprise Active Call Widget.
//
// Replaces the compact "ActiveCallPill" with a full call-control surface:
// Hold / Resume, Mute / Unmute, Manual Record (start/pause/resume/stop),
// DTMF keypad, Live Notes, Mandatory Disposition, and End Call.
//
// The widget still polls /api/telephony/active-call for the parent
// call-log SSOT, then augments with per-tick reads from
// /api/telephony/calls/{sid}/status for the fine-grained state
// (on_hold / muted / recording_state / disposition).
//
// Every button maps 1:1 to a backend endpoint at
// /api/telephony/calls/{sid}/…  which writes a system_audit row.
//
// UX rules:
//   * End Call is DISABLED until a disposition is picked.
//   * DTMF/Notes open inline sub-panels; nothing modal (widget must
//     remain visible across pages).
//   * Buttons enable/disable according to state — e.g., Pause is only
//     visible when recording_state=='recording'; Resume replaces Hold.
//   * Minimizes to a floating icon for full-screen work, expands on
//     click.  All existing data-testid attributes for the pill are
//     preserved so downstream automation keeps working.

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, PhoneForwarded, PhoneOff, PauseCircle, PlayCircle,
  Mic, MicOff, Grid3x3, StickyNote, ClipboardCheck, ChevronUp,
  ChevronDown, Minimize2, Users, UserPlus, Clock, ArrowLeftRight,
  ClipboardList, MoreHorizontal, BookOpen, CalendarPlus, Mail,
} from 'lucide-react';
import OnboardingCallModal from './OnboardingCallModal';   // v3.19-1416 1PM Phase 2 (deprecated in v3.21-1620; kept import for compat but no longer rendered)
import CallAssistPanel from './CallAssistPanel';           // v3.21-1612 · SSOT
import EmailComposeDialog from './EmailComposeDialog';     // v3.21-1612 · SSOT
import api from '../lib/api';
import TransferCallModal from './TransferCallModal';
import { toast } from 'sonner';
// v3.21-1620 · route Notes/Onboarding through the standard Client Workspace.
import { useClientWorkspace } from '../context/ClientWorkspaceContext';

const POLL_MS = 5000;
const STATUS_POLL_MS = 3000;

// v3.21-1612 · Canonical rep-facing dispositions.  These are the ONLY
// codes surfaced in the Dispose panel — legacy codes remain valid in
// the backend for historical call_logs but must not clutter the UI.
const DISPOSITIONS = [
  ['no_answer',            'No Answer'],
  ['left_voicemail',       'Voicemail Left'],
  ['spoke_not_interested', 'Spoke - Not Interested'],
  ['spoke_interested',     'Spoke - Interested'],
  ['touched_base',         'Touched Base'],
  ['call_back',            'Call Back'],
];

const fmtDuration = (sec) => {
  if (sec < 0 || sec == null || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const fmtPhone = (raw) => {
  if (!raw) return '';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  return raw;
};

// A subtle status pill next to the duration.
function StatusChip({ label, tone }) {
  const tones = {
    live:    'bg-emerald-100 text-emerald-800 border-emerald-300',
    hold:    'bg-amber-100 text-amber-800 border-amber-300',
    mute:    'bg-slate-200 text-slate-800 border-slate-300',
    record:  'bg-red-100 text-red-800 border-red-300',
    paused:  'bg-yellow-100 text-yellow-800 border-yellow-300',
    warn:    'bg-orange-100 text-orange-800 border-orange-300',
  };
  return (
    <span
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${tones[tone] || tones.live}`}
      data-testid={`call-status-chip-${tone}`}
    >
      {label}
    </span>
  );
}

// A compact circular button used inside the control grid.
function CtrlButton({ label, icon: Icon, onClick, active, disabled, danger, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      data-testid={testId}
      className={[
        'flex flex-col items-center gap-1 rounded-lg py-2 px-1',
        'text-[10px] font-semibold uppercase tracking-wider',
        'transition-colors',
        active
          ? 'bg-emerald-600 text-white'
          : danger
            ? 'bg-red-600 text-white hover:bg-red-700'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
        disabled ? 'opacity-40 cursor-not-allowed' : '',
      ].join(' ')}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
}

// v3.19-1413 — Compliance Recording Status (READ-ONLY).
//
// Displays the always-on recording state to the operator with three
// fields: Status, Duration, Health.  There are NO controls — operators
// must not be able to stop mandatory compliance recording.
function RecordingStatusPanel({ state, durationSec }) {
  const s = (state || 'idle').toLowerCase();
  // Map backend recording_state → operator-facing UX
  const isHealthy = s === 'recording';
  const isPaused  = s === 'paused';
  const isError   = s === 'error' || s === 'failed';
  const isIdle    = s === 'idle' || s === 'stopped';

  const statusText  = isHealthy ? 'Recording'
                    : isPaused  ? 'Paused (system)'
                    : isError   ? 'Error'
                    : 'Starting…';
  const statusTone  = isHealthy ? 'bg-red-50 text-red-700 border-red-200'
                    : isPaused  ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : isError   ? 'bg-slate-100 text-slate-600 border-slate-300'
                    : 'bg-slate-50 text-slate-500 border-slate-200';
  const dot         = isHealthy ? 'bg-red-500 animate-pulse'
                    : isPaused  ? 'bg-amber-500'
                    : isError   ? 'bg-slate-400'
                    : 'bg-slate-300';
  const health      = isHealthy ? 'Healthy'
                    : isPaused  ? 'Attention'
                    : isError   ? 'Failed — ops notified'
                    : 'Initializing';
  const fmt = (n) => {
    const m = Math.floor((n || 0) / 60).toString().padStart(2,'0');
    const s = Math.floor((n || 0) % 60).toString().padStart(2,'0');
    return `${m}:${s}`;
  };
  // Recording begins with the call (record-from-start).  Duration
  // mirrors call duration once the recording is live; idle/error
  // rows show 00:00.
  const shownDuration = (isHealthy || isPaused) ? fmt(durationSec) : '00:00';

  return (
    <div
      className={`border rounded-lg px-3 py-2 flex items-center justify-between gap-3 ${statusTone}`}
      data-testid="compliance-recording-panel"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
            Compliance Recording
          </div>
          <div className="text-sm font-semibold truncate" data-testid="compliance-recording-status">
            {statusText}
          </div>
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-wider opacity-80">Duration</div>
        <div className="text-sm font-mono tabular-nums" data-testid="compliance-recording-duration">
          {shownDuration}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-wider opacity-80">Health</div>
        <div className="text-sm font-semibold" data-testid="compliance-recording-health">
          {health}
        </div>
      </div>
    </div>
  );
}


export default function ActiveCallPill() {
  const [active, setActive] = useState(null);
  // v3.21-1620 · Access the standard Client Workspace so Notes /
  // Onboarding / Send Email flow through ONE authoritative window.
  const { openClientWorkspace, attachCallToWorkspace } = useClientWorkspace();
  const [expanded, setExpanded] = useState(true);
  const [minimized, setMinimized] = useState(false);
  // v3.21-1618 · Global Call Workspace launcher — an external trigger
  // (see `openCallWorkspace()` below) forces the pill open in IDLE mode
  // when no active/wrap-up call exists, so the rep can access recent
  // calls, Send Email, Callback, Book Appointment, Call Assist without
  // starting a fake call.
  const [idleOpen, setIdleOpen] = useState(false);
  const [recentCalls, setRecentCalls] = useState([]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [tickNow, setTickNow] = useState(Date.now());
  const sawCallRef = useRef(false);

  // Call-control state (live, from /calls/{sid}/status)
  const [ctrl, setCtrl] = useState({
    on_hold: false, muted: false, recording_state: 'idle',
    disposition: null, in_conference: false, warm_transfer_state: null,
    notes_count: 0,
  });
  // v3.21-1612 · End Call idempotency guard — double-click cannot fire twice.
  const [ending, setEnding] = useState(false);
  // v3.21-1612 · Call Assist / Book Appointment / Send Email
  const [callAssistOpen, _setCallAssistOpen] = useState(false);
  const [emailOpen, _setEmailOpen] = useState(false);
  const navigate = useNavigate();
  // Sub-panels
  const [panel, setPanel] = useState(null); // 'dtmf' | 'notes' | 'disposition' | 'transfer' | 'participant' | 'callback' | null
  // v3.19-1416 1PM UPDATE Phase 2 — Onboarding Call modal open state.
  // v3.21-1619 · Persist across pill remounts via localStorage so a
  // Complete-Call transition or a refresh no longer closes the large
  // working workspace.
  const [onboardingOpen, _setOnboardingOpen] = useState(false);
  const _sidForModals = active?.twilio_sid || 'idle';
  const setOnboardingOpen = useCallback((v) => {
    _setOnboardingOpen(v);
    try {
      const k = `activeCall.modal.onboarding:${_sidForModals}`;
      if (v) window.localStorage.setItem(k, '1');
      else window.localStorage.removeItem(k);
    } catch (_err) { void _err; }
  }, [_sidForModals]);
  const setEmailOpen = useCallback((v) => {
    _setEmailOpen(v);
    try {
      const k = `activeCall.modal.email:${_sidForModals}`;
      if (v) window.localStorage.setItem(k, '1');
      else window.localStorage.removeItem(k);
    } catch (_err) { void _err; }
  }, [_sidForModals]);
  const setCallAssistOpen = useCallback((v) => {
    _setCallAssistOpen(v);
    try {
      const k = `activeCall.modal.assist:${_sidForModals}`;
      if (v) window.localStorage.setItem(k, '1');
      else window.localStorage.removeItem(k);
    } catch (_err) { void _err; }
  }, [_sidForModals]);
  useEffect(() => {
    try {
      if (window.localStorage.getItem(`activeCall.modal.onboarding:${_sidForModals}`) === '1') _setOnboardingOpen(true);
      if (window.localStorage.getItem(`activeCall.modal.email:${_sidForModals}`) === '1') _setEmailOpen(true);
      if (window.localStorage.getItem(`activeCall.modal.assist:${_sidForModals}`) === '1') _setCallAssistOpen(true);
    } catch (_err) { void _err; }
  }, [_sidForModals]);
  const [dtmfBuffer, setDtmfBuffer] = useState('');
  const [noteText, setNoteText] = useState('');
  const [dispCode, setDispCode] = useState('');
  const [dispDetail, setDispDetail] = useState('');
  // Phase B panel state
  const [xferTo, setXferTo] = useState('');
  const [xferKind, setXferKind] = useState('warm'); // 'warm' | 'blind'
  const [addPartTo, setAddPartTo] = useState('');
  const [addPartMuted, setAddPartMuted] = useState(false);
  const [cbWhen, setCbWhen] = useState('');
  const [cbNote, setCbNote] = useState('');
  // v3.21-1617 · Idempotency guard for Complete Call.
  const [completing, setCompleting] = useState(false);

  // v3.21-1617 · Notes autosave — debounced localStorage draft keyed
  // by twilio_sid.  On the first fetchActive the draft is rehydrated
  // if the sid matches.  Draft is cleared once the note is persisted
  // through /notes.  Prevents lost work on refresh / navigation / PWA
  // reload during wrap-up.
  const DRAFT_KEY = (sid) => `activeCall.notesDraft:${sid || 'none'}`;
  useEffect(() => {
    if (!active?.twilio_sid) return;
    // Only rehydrate when the local note buffer is empty — never stomp
    // on what the rep is actively typing.
    try {
      const saved = window.localStorage.getItem(DRAFT_KEY(active.twilio_sid));
      if (saved && !noteText) setNoteText(saved);
    } catch (_err) { void _err; }
  }, [active?.twilio_sid]);
  useEffect(() => {
    if (!active?.twilio_sid) return undefined;
    const h = setTimeout(() => {
      try {
        if (noteText) {
          window.localStorage.setItem(DRAFT_KEY(active.twilio_sid), noteText);
        } else {
          window.localStorage.removeItem(DRAFT_KEY(active.twilio_sid));
        }
      } catch (_err) { void _err; }
    }, 250);
    return () => clearTimeout(h);
  }, [noteText, active?.twilio_sid]);

  // Poll active-call SSOT
  const fetchActive = useCallback(async () => {
    try {
      const res = await api.get('/telephony/active-call');
      const a = res.data?.active || null;
      setActive((prev) => {
        if (a && (!prev || prev.call_log_id !== a.call_log_id)) {
          setExpanded(true);
          setMinimized(false);
          sawCallRef.current = true;
          setPanel(null);
          setDtmfBuffer('');
          setNoteText('');
          setDispCode('');
          setDispDetail('');
        }
        return a;
      });
    } catch { /* soft-fail */ }
  }, []);

  useEffect(() => {
    fetchActive();
    const id = setInterval(fetchActive, POLL_MS);
    return () => clearInterval(id);
  }, [fetchActive]);

  // v3.21-1620 · If the Client Workspace is already open for THIS
  // client (opened before the call started, or reopened during
  // wrap-up), attach the active call SID so the workspace shows the
  // Live/Wrap-Up strip and the Notes surface stamps the call SID.
  //
  // v3.21-1623 · Auto-OPEN the workspace when a NEW active inbound
  // call arrives for a known client — the rep should not have to hunt
  // for the call.  We only auto-open on transition (fresh SID) so we
  // don't clobber a workspace the rep has already closed on purpose.
  const lastAutoOpenSidRef = useRef(null);
  useEffect(() => {
    if (active?.client_id) {
      attachCallToWorkspace(active.client_id, active?.twilio_sid || null);
      const direction = (active?.direction || '').toLowerCase();
      const state     = (active?.workflow_state || active?.status || '').toLowerCase();
      const isNewIncoming =
        active?.twilio_sid &&
        lastAutoOpenSidRef.current !== active.twilio_sid &&
        direction === 'inbound' &&
        state in { ringing: 1, active: 1, in_progress: 1 };
      if (isNewIncoming) {
        lastAutoOpenSidRef.current = active.twilio_sid;
        try {
          window.dispatchEvent(new CustomEvent('client-workspace:open', {
            detail: { clientId: active.client_id, callSid: active.twilio_sid },
          }));
        } catch (_e) { /* noop */ }
      }
    } else if (active?.twilio_sid) {
      // v3.21-1624 · Inbound call with NO resolved client → surface the
      // Unknown Caller pane automatically (idempotent per SID).
      const direction = (active?.direction || '').toLowerCase();
      const state     = (active?.workflow_state || active?.status || '').toLowerCase();
      const isNewUnknown =
        lastAutoOpenSidRef.current !== active.twilio_sid &&
        direction === 'inbound' &&
        state in { ringing: 1, active: 1, in_progress: 1 };
      if (isNewUnknown) {
        lastAutoOpenSidRef.current = active.twilio_sid;
        try {
          window.dispatchEvent(new CustomEvent('unknown-caller:open', {
            detail: {
              callSid: active.twilio_sid,
              phone:   active?.from || active?.phone_number || 'Unknown',
            },
          }));
        } catch (_e) { /* noop */ }
      }
    }
  }, [active?.client_id, active?.twilio_sid, active?.direction, active?.workflow_state, active?.status, active?.from, active?.phone_number, attachCallToWorkspace]);

  // v3.21-1618 · Listen for the global "open-call-workspace" event
  // dispatched by the DashboardLayout header launcher.  If an active
  // or wrap-up call already exists, we simply un-minimize; otherwise
  // we open the IDLE state.
  useEffect(() => {
    const handler = async () => {
      setMinimized(false);
      setExpanded(true);
      if (!active) {
        setIdleOpen(true);
        try {
          const r = await api.get('/telephony/call-history?limit=5');
          setRecentCalls(r.data?.items || r.data || []);
        } catch (_err) { void _err; }
      }
    };
    window.addEventListener('open-call-workspace', handler);
    return () => window.removeEventListener('open-call-workspace', handler);
  }, [active]);

  // Close idle when a real call arrives so the two states never collide.
  useEffect(() => { if (active) setIdleOpen(false); }, [active]);

  // 1s duration tick
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  // Poll call-control state
  const fetchCtrl = useCallback(async () => {
    if (!active?.twilio_sid) return;
    try {
      const res = await api.get(`/telephony/calls/${active.twilio_sid}/status`);
      setCtrl({
        on_hold:             !!res.data?.on_hold,
        muted:               !!res.data?.muted,
        recording_state:     res.data?.recording_state || 'idle',
        disposition:         res.data?.disposition || null,
        in_conference:       !!res.data?.in_conference,
        warm_transfer_state: res.data?.warm_transfer_state || null,
        notes_count:         Number(res.data?.notes_count || 0),
      });
    } catch { /* soft-fail */ }
  }, [active?.twilio_sid]);
  useEffect(() => {
    if (!active?.twilio_sid) return undefined;
    fetchCtrl();
    const id = setInterval(fetchCtrl, STATUS_POLL_MS);
    return () => clearInterval(id);
  }, [active?.twilio_sid, fetchCtrl]);

  const post = async (path, body, action) => {
    try {
      const res = await api.post(path, body || {});
      await fetchCtrl();
      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || `${action} failed`;
      toast.error(msg);
      return null;
    }
  };

  const doHold        = () => post(`/telephony/calls/${active.twilio_sid}/hold`,   null, 'Hold');
  const doResume      = () => post(`/telephony/calls/${active.twilio_sid}/resume`, null, 'Resume');
  const doMute        = () => post(`/telephony/calls/${active.twilio_sid}/mute`,   null, 'Mute');
  const doUnmute      = () => post(`/telephony/calls/${active.twilio_sid}/unmute`, null, 'Unmute');
  // v3.19-1413 — Manual Record/Pause/Resume/Stop controls REMOVED.
  // Compliance recording is always-on (Conference-First architecture,
  // record="record-from-start").  Operators must not be able to
  // accidentally stop mandatory recordings.  Read-only Recording
  // Status / Duration / Health is rendered below.

  const doSendDtmf = async () => {
    if (!dtmfBuffer) return;
    const ok = await post(`/telephony/calls/${active.twilio_sid}/dtmf`, { digits: dtmfBuffer }, 'DTMF');
    if (ok) {
      toast.success(`Sent ${dtmfBuffer}`);
      setDtmfBuffer('');
    }
  };
  const doSaveNote = async () => {
    if (!noteText.trim()) return;
    const ok = await post(`/telephony/calls/${active.twilio_sid}/notes`, { text: noteText.trim() }, 'Save note');
    if (ok) {
      toast.success('Note saved');
      setNoteText('');
      // v3.21-1617 · Clear the local draft once the note is persisted.
      try { window.localStorage.removeItem(DRAFT_KEY(active.twilio_sid)); } catch (_err) { void _err; }
      setPanel(null);
    }
  };
  const doSaveDisposition = async () => {
    if (!dispCode) { toast.error('Pick a disposition'); return; }
    const ok = await post(`/telephony/calls/${active.twilio_sid}/disposition`,
                          { code: dispCode, detail: dispDetail || null }, 'Disposition');
    if (ok) {
      toast.success('Disposition saved');
      // v3.21-1612 · Call Back disposition automatically opens the
      // existing Callback panel so the rep can schedule the return
      // call.  Reuses the SSOT callback workflow — no new UI.
      if (dispCode === 'call_back') { setPanel('callback'); }
      else { setPanel(null); }
    }
  };
  const doEnd = async () => {
    // v3.21-1612 · Unified completion gate.
    //   1. Disposition must be set.
    //   2. Notes must be non-empty (the ActiveCallPill Notes SSOT).
    if (!ctrl.disposition) {
      toast.error('Set a disposition before ending the call');
      setPanel('disposition');
      return;
    }
    const hasNotes = (ctrl.notes_count || 0) > 0;
    if (!hasNotes) {
      toast.error('Notes are required before completing this call.');
      setPanel('notes');
      return;
    }
    if (ending) return;                                            // idempotency
    setEnding(true);
    // v3.21-1617 · End Call disconnects telephony only; the workspace
    // transitions into wrap-up and stays open until Complete Call.
    const ok = await post(`/telephony/calls/${active.twilio_sid}/end`, null, 'End call');
    if (ok) { setTimeout(fetchActive, 800); }
    setTimeout(() => setEnding(false), 2500);
  };

  // v3.21-1617 · Complete Call — finalize the rep-side documentation.
  // Only after this call does the workspace close.
  const doComplete = async () => {
    // Auto-flush any un-saved local Notes so the completion gate can
    // read them from the DB.
    if (noteText.trim()) {
      await post(`/telephony/calls/${active.twilio_sid}/notes`,
                 { text: noteText.trim() }, 'Save note');
      setNoteText('');
      try { window.localStorage.removeItem(DRAFT_KEY(active.twilio_sid)); } catch (_err) { void _err; }
      await fetchCtrl();
    }
    if (!ctrl.disposition) {
      toast.error('Set a disposition before completing');
      setPanel('disposition');
      return;
    }
    if ((ctrl.notes_count || 0) === 0 && !noteText.trim()) {
      toast.error('Add call Notes before completing this call.');
      setPanel('notes');
      return;
    }
    if (completing) return;
    setCompleting(true);
    const ok = await post(`/telephony/calls/${active.twilio_sid}/complete-workflow`,
                          null, 'Complete call');
    if (ok) { toast.success('Call completed'); setTimeout(fetchActive, 500); }
    setTimeout(() => setCompleting(false), 2500);
  };

  // Phase B — Warm/Blind transfer, Add Participant, Callback
  const doStartTransfer = async () => {
    if (!xferTo.trim()) { toast.error('Enter a destination phone number'); return; }
    const endpoint = xferKind === 'blind' ? 'blind-transfer' : 'warm-transfer';
    if (xferKind === 'warm' && !ctrl.on_hold) {
      toast.error('Place the customer on Hold before starting a warm transfer');
      return;
    }
    const ok = await post(`/telephony/calls/${active.twilio_sid}/${endpoint}`,
                          { to_number: xferTo.trim() }, `${xferKind} transfer`);
    if (ok) {
      toast.success(xferKind === 'blind' ? 'Transferred — you can hang up' : 'Connected to third party');
      if (xferKind === 'blind') { setTimeout(fetchActive, 1500); }
      setPanel(null); setXferTo('');
    }
  };
  const doCompleteWarm = () => post(`/telephony/calls/${active.twilio_sid}/warm-transfer/complete`, null, 'Complete transfer')
    .then(ok => ok && setTimeout(fetchActive, 1500));
  const doReturnWarm = () => post(`/telephony/calls/${active.twilio_sid}/warm-transfer/return`, null, 'Return to caller');

  const doAddParticipant = async () => {
    if (!addPartTo.trim()) { toast.error('Enter a destination phone number'); return; }
    const ok = await post(`/telephony/calls/${active.twilio_sid}/add-participant`,
                          { to_number: addPartTo.trim(), muted: addPartMuted }, 'Add participant');
    if (ok) { toast.success('Participant added'); setAddPartTo(''); setPanel(null); }
  };

  const doCallback = async () => {
    if (!cbWhen) { toast.error('Pick a date/time'); return; }
    // Convert local datetime-local to ISO UTC
    let iso;
    try { iso = new Date(cbWhen).toISOString(); } catch { toast.error('Invalid date'); return; }
    const ok = await post(`/telephony/calls/${active.twilio_sid}/callback`,
                          { when: iso, note: cbNote || null }, 'Callback');
    if (ok) { toast.success('Callback scheduled'); setCbNote(''); setCbWhen(''); setPanel(null); }
  };

  // v3.21-1618 · If no active/wrap-up call AND idleOpen was triggered,
  // render an IDLE workspace so the rep can Send Email / Callback /
  // Book Appointment / Call Assist for a recent call.
  if (!active && idleOpen) {
    return (
      <div
        className="fixed bottom-6 right-6 z-[60] bg-white rounded-2xl shadow-2xl border-2 border-slate-300 overflow-hidden"
        style={{ width: 380 }}
        data-testid="active-call-pill-idle"
        data-workflow-state="idle"
      >
        <div className="flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-slate-700 to-slate-800 text-white">
          <Phone className="h-4 w-4" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider opacity-90"
                 data-testid="active-call-header-label">Call Workspace</div>
            <div className="text-sm font-semibold">No active call</div>
          </div>
          <button
            type="button"
            onClick={() => setIdleOpen(false)}
            className="p-1 rounded hover:bg-white/10"
            aria-label="Close"
            data-testid="idle-workspace-close"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
        </div>
        <div className="px-3 py-3 space-y-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Recent calls
          </div>
          <div className="max-h-52 overflow-auto space-y-1.5" data-testid="idle-recent-calls">
            {(recentCalls || []).length === 0 && (
              <div className="text-xs text-slate-500 italic">No recent calls in the last few days.</div>
            )}
            {(recentCalls || []).slice(0, 5).map((c) => (
              <button
                key={c.id || c.twilio_sid}
                type="button"
                onClick={() => {
                  if (c.client_id) navigate(`/clients/${c.client_id}`);
                }}
                className="w-full flex items-center justify-between gap-2 border rounded-md px-2 py-1.5 hover:bg-slate-50 text-left"
                data-testid={`idle-recent-call-${c.id || c.twilio_sid}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-slate-800 truncate">
                    {c.client_name || c.client_number || 'Unknown contact'}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {(c.direction || c.type || 'outbound').toUpperCase()}
                    {c.disposition ? ` · ${c.disposition}` : ''}
                    {c.created_at ? ` · ${new Date(c.created_at).toLocaleString()}` : ''}
                  </div>
                </div>
                {c.client_id && <Mail className="h-3.5 w-3.5 text-sky-600 shrink-0" />}
              </button>
            ))}
          </div>
          <div className="pt-2 border-t grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => setCallAssistOpen(true)}
              className="flex flex-col items-center gap-1 py-2 border rounded-md hover:bg-slate-100 text-xs"
              data-testid="idle-call-assist"
            >
              <BookOpen className="h-4 w-4 text-indigo-600" />
              <span>Call Assist</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const withClient = (recentCalls || []).find(c => c.client_id);
                if (withClient) navigate(`/clients/${withClient.client_id}#appointments`);
                else toast.error('Open a recent call to book an appointment');
              }}
              className="flex flex-col items-center gap-1 py-2 border rounded-md hover:bg-slate-100 text-xs"
              data-testid="idle-book-appointment"
            >
              <CalendarPlus className="h-4 w-4 text-emerald-600" />
              <span>Book Appt</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const withClient = (recentCalls || []).find(c => c.client_id);
                if (withClient) setEmailOpen(true);
                else toast.error('Open a recent call to send an email');
              }}
              className="flex flex-col items-center gap-1 py-2 border rounded-md hover:bg-slate-100 text-xs"
              data-testid="idle-send-email"
            >
              <Mail className="h-4 w-4 text-sky-600" />
              <span>Send Email</span>
            </button>
          </div>
        </div>
        <CallAssistPanel
          open={callAssistOpen}
          onClose={() => setCallAssistOpen(false)}
          clientId={(recentCalls || []).find(c => c.client_id)?.client_id}
        />
        <EmailComposeDialog
          open={emailOpen}
          onOpenChange={setEmailOpen}
          clientId={(recentCalls || []).find(c => c.client_id)?.client_id}
          clientName={(recentCalls || []).find(c => c.client_id)?.client_name}
        />
      </div>
    );
  }

  if (!active) return null;

  // v3.21-1613 · Direction awareness — inbound calls flow through the
  // same widget so the rep gets the full v3.21-1612 workflow whether
  // they placed the call or answered one.  `direction` defaults to
  // 'outbound' for legacy call_logs rows that pre-date the field.
  const direction   = (active?.direction || active?.type || 'outbound').toLowerCase();
  const isInbound   = direction === 'inbound';
  const ringingState = (active?.status || '').toLowerCase() === 'ringing';

  // v3.21-1617 · Wrap-Up mode awareness.  Once telephony ends, backend
  // sets workflow_state=wrap_up and keeps returning the call from
  // /telephony/active-call so this workspace stays open until the rep
  // clicks Complete Call.
  const workflowState = (active?.workflow_state || 'active').toLowerCase();
  const isWrapUp   = workflowState === 'wrap_up';
  const telephonyEnded = (active?.status || '').toLowerCase() === 'completed' || isWrapUp;
  const controlsLocked = telephonyEnded; // Hold / Mute / Transfer / Keypad / Add Party
  const headerLabel = isWrapUp
    ? 'Call Ended — Wrap-Up'
    : (isInbound ? 'Incoming call' : 'Call in progress');

  const startedMs = (() => {
    try { return new Date(active.created_at).getTime(); } catch { return tickNow; }
  })();
  const durationSec = Math.max(0, Math.floor((tickNow - startedMs) / 1000));

  if (minimized) {
    // v3.21-1617 · Persistent global reopen — pill remains reachable
    // in wrap-up so a rep can navigate the Hub and come back.
    const wrapClasses = isWrapUp
      ? 'bg-amber-600 hover:bg-amber-700'
      : (isInbound ? 'bg-sky-600 hover:bg-sky-700' : 'bg-emerald-600 hover:bg-emerald-700');
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-3 h-11 rounded-full text-white shadow-lg ${wrapClasses}`}
        data-testid="active-call-pill-minimized"
        aria-label={isWrapUp
          ? `Call Wrap-Up — ${active.client_name || 'Unknown contact'}`
          : (isInbound ? 'Show incoming call' : 'Show active call')}
      >
        <Phone className={`h-5 w-5 ${!isWrapUp ? 'animate-pulse' : ''}`} />
        <span className="text-xs font-semibold whitespace-nowrap max-w-[180px] truncate">
          {isWrapUp ? 'Wrap-Up' : (isInbound ? 'Incoming' : 'On Call')}
          {' · '}
          {active.client_name || 'Unknown'}
        </span>
      </button>
    );
  }

  const panelBorder = isWrapUp
    ? 'border-amber-300'
    : (isInbound ? 'border-sky-300' : 'border-emerald-300');
  const panelHeaderBg = isWrapUp
    ? 'bg-gradient-to-r from-amber-600 to-amber-700'
    : (isInbound
        ? 'bg-gradient-to-r from-sky-600 to-sky-700'
        : 'bg-gradient-to-r from-emerald-600 to-emerald-700');

  return (
    <>
      <div
        className={`fixed bottom-6 right-6 z-[60] bg-white rounded-2xl shadow-2xl border-2 overflow-hidden ${panelBorder}`}
        style={{ width: 380 }}
        data-testid="active-call-pill"
        data-workflow-state={workflowState}
      >
        {/* Header — status + duration */}
        <div className={`flex items-center gap-2 px-3 py-2.5 text-white ${panelHeaderBg}`}>
          <div className="relative">
            <Phone className="h-4 w-4" />
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-400 animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider opacity-90 flex items-center gap-1.5">
              <span data-testid="active-call-header-label">{headerLabel}</span>
              {/* v3.21-1613 · direction badge  · v3.21-1617 · WRAP-UP badge */}
              <span
                className={`text-[9px] font-black px-1.5 py-0.5 rounded border uppercase tracking-wider ${
                  isWrapUp
                    ? 'bg-amber-100 text-amber-900 border-amber-300'
                    : isInbound
                      ? 'bg-sky-100 text-sky-800 border-sky-300'
                      : 'bg-emerald-100 text-emerald-800 border-emerald-300'
                }`}
                data-testid={`active-call-direction-${isWrapUp ? 'wrap-up' : direction}`}
              >
                {isWrapUp ? 'Wrap-Up' : (isInbound ? 'Inbound' : 'Outbound')}
              </span>
              {ringingState && isInbound && <StatusChip label="Ringing" tone="warn" />}
              {ctrl.on_hold && <StatusChip label="On Hold" tone="hold" />}
              {ctrl.muted && <StatusChip label="Muted" tone="mute" />}
              {ctrl.in_conference && <StatusChip label="Conf" tone="live" />}
              {ctrl.warm_transfer_state === 'consulting' && <StatusChip label="Consult" tone="warn" />}
              {ctrl.recording_state === 'recording' && <StatusChip label="REC" tone="record" />}
              {ctrl.recording_state === 'paused' && <StatusChip label="REC Paused" tone="paused" />}
              {!ctrl.disposition && <StatusChip label="No Disp" tone="warn" />}
            </div>
            <div className="text-sm font-semibold truncate" data-testid="active-call-client-name">
              {active.client_name || 'Unknown contact'}
            </div>
          </div>
          <div className="text-sm font-mono tabular-nums" data-testid="active-call-duration">
            {fmtDuration(durationSec)}
          </div>
          {/* v3.19-1420 1PM Phase 2c — Onboarding launcher available in the
              PILL HEADER too, so the button is reachable even when the
              rep collapses the control grid.  Zero-click access from
              every UI state of the pill (expanded, collapsed, or
              minimized-then-restored). */}
          <button
            type="button"
            onClick={() => {
              if (active?.client_id) {
                openClientWorkspace(active.client_id, { callSid: active?.twilio_sid });
              } else {
                toast.error('No client attached to this call');
              }
            }}
            className="p-1 rounded hover:bg-white/10 text-white"
            title="Open Client Workspace"
            aria-label="Open Client Workspace"
            data-testid="active-call-header-onboarding"
          >
            <ClipboardList className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setExpanded((v) => !v)}
            className="p-1 rounded hover:bg-white/10" aria-label={expanded ? 'Collapse' : 'Expand'}
            data-testid="active-call-toggle-expand">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          <button type="button" onClick={() => setMinimized(true)}
            className="p-1 rounded hover:bg-white/10" aria-label="Minimize"
            data-testid="active-call-minimize">
            <Minimize2 className="h-4 w-4" />
          </button>
        </div>

        {expanded && (
          <div className="px-3 py-3 space-y-3">
            {/* Contact details */}
            <div className="space-y-0.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Phone</div>
              <div className="text-sm font-mono text-slate-800" data-testid="active-call-phone">
                {fmtPhone(active.client_number)}
              </div>
            </div>

            {/* Primary control grid — v3.21-1617 · telephony-only
                controls disable in Wrap-Up mode. */}
            <div className="grid grid-cols-4 gap-1.5">
              {ctrl.on_hold ? (
                <CtrlButton label="Resume" icon={PlayCircle} onClick={doResume} active testId="ctrl-resume" disabled={controlsLocked} />
              ) : (
                <CtrlButton label="Hold" icon={PauseCircle} onClick={doHold} testId="ctrl-hold" disabled={controlsLocked} />
              )}
              {ctrl.muted ? (
                <CtrlButton label="Unmute" icon={Mic} onClick={doUnmute} active testId="ctrl-unmute" disabled={controlsLocked} />
              ) : (
                <CtrlButton label="Mute" icon={MicOff} onClick={doMute} testId="ctrl-mute" disabled={controlsLocked} />
              )}
              <CtrlButton label="Transfer" icon={PhoneForwarded} onClick={() => setPanel(panel==='transfer'?null:'transfer')} active={panel==='transfer'} testId="ctrl-transfer" disabled={controlsLocked} />
              <CtrlButton label="Keypad" icon={Grid3x3} onClick={() => setPanel(panel==='dtmf'?null:'dtmf')} active={panel==='dtmf'} testId="ctrl-dtmf" disabled={controlsLocked} />

              {/* v3.19-1413 — Compliance recording is ALWAYS-ON.
                  Manual Record/Pause/Resume/Stop controls removed so
                  operators cannot accidentally stop the mandatory
                  recording.  See read-only Recording panel below.
                  v3.21-1620 · Notes now open the STANDARD Client
                  Workspace — the pill no longer maintains a second
                  Notes textarea/state.  A saved authoritative Note
                  bumps `notes_count` via the same `/notes` API. */}
              <CtrlButton label="Notes" icon={StickyNote}
                onClick={() => {
                  if (active?.client_id) {
                    openClientWorkspace(active.client_id, { callSid: active?.twilio_sid });
                  } else {
                    toast.error('No client attached to this call');
                  }
                }}
                active={(ctrl.notes_count||0) > 0}
                testId="ctrl-notes" />
              <CtrlButton label={ctrl.disposition ? 'Dispo ✓' : 'Dispose'} icon={ClipboardCheck}
                onClick={() => setPanel(panel==='disposition'?null:'disposition')} active={panel==='disposition' || !!ctrl.disposition}
                testId="ctrl-disposition" />

              {/* Phase B row */}
              <CtrlButton label="Add Party" icon={UserPlus}
                onClick={() => setPanel(panel==='participant'?null:'participant')}
                active={panel==='participant'} testId="ctrl-add-participant"
                disabled={controlsLocked} />
              <CtrlButton label="Callback" icon={Clock}
                onClick={() => setPanel(panel==='callback'?null:'callback')}
                active={panel==='callback'} testId="ctrl-callback" />
              {/* v3.21-1620 · Onboarding is one tool inside the
                  standard Client Workspace, not a separate window. */}
              <CtrlButton label="Client Workspace" icon={ClipboardList}
                onClick={() => {
                  if (active?.client_id) {
                    openClientWorkspace(active.client_id, { callSid: active?.twilio_sid });
                  } else {
                    toast.error('No client attached to this call');
                  }
                }}
                testId="ctrl-onboarding-call" />
              {/* v3.21-1612 · More menu — Call Assist · Book Appt · Send Email.
                  Reuses existing implementations, never duplicates them. */}
              <CtrlButton label="More" icon={MoreHorizontal}
                onClick={() => setPanel(panel==='more'?null:'more')}
                active={panel==='more'} testId="ctrl-more" />
            </div>

            {/* More sub-panel · v3.21-1612 */}
            {panel === 'more' && (
              <div className="border-t pt-3 grid grid-cols-3 gap-1.5" data-testid="more-panel">
                <button type="button"
                  onClick={() => { setCallAssistOpen(true); setPanel(null); }}
                  className="flex flex-col items-center gap-1 py-2 border rounded-md hover:bg-slate-100 text-xs"
                  data-testid="more-call-assist">
                  <BookOpen className="h-4 w-4 text-indigo-600" />
                  <span>Call Assist</span>
                </button>
                <button type="button"
                  onClick={() => {
                    if (active?.client_id) {
                      navigate(`/clients/${active.client_id}#appointments`);
                      setPanel(null);
                    } else toast.error('No client attached to this call');
                  }}
                  className="flex flex-col items-center gap-1 py-2 border rounded-md hover:bg-slate-100 text-xs"
                  data-testid="more-book-appointment">
                  <CalendarPlus className="h-4 w-4 text-emerald-600" />
                  <span>Book Appt</span>
                </button>
                <button type="button"
                  onClick={() => {
                    if (active?.client_id) { setEmailOpen(true); setPanel(null); }
                    else toast.error('No client attached to this call');
                  }}
                  className="flex flex-col items-center gap-1 py-2 border rounded-md hover:bg-slate-100 text-xs"
                  data-testid="more-send-email">
                  <Mail className="h-4 w-4 text-sky-600" />
                  <span>Send Email</span>
                </button>
              </div>
            )}

            {/* v3.19-1413 — Read-only Compliance Recording panel.
                Always-on; operator has no controls.  Health, Status,
                and Duration are surfaced so operators can SEE that
                the mandatory recording is active but cannot alter it. */}
            <RecordingStatusPanel
              state={ctrl.recording_state}
              durationSec={durationSec}
            />

            {/* Warm transfer in-progress banner */}
            {ctrl.warm_transfer_state === 'consulting' && (
              <div className="border-t pt-2 flex items-center gap-2" data-testid="warm-transfer-banner">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-xs text-blue-800 flex-1">Consulting with 3rd party…</span>
                <button type="button" onClick={doReturnWarm}
                  className="text-xs font-semibold px-2 py-1 bg-slate-200 rounded hover:bg-slate-300"
                  data-testid="warm-transfer-return">Return</button>
                <button type="button" onClick={doCompleteWarm}
                  className="text-xs font-semibold px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                  data-testid="warm-transfer-complete">Complete</button>
              </div>
            )}

            {/* DTMF sub-panel */}
            {panel === 'dtmf' && (
              <div className="border-t pt-3 space-y-2" data-testid="dtmf-panel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Send DTMF</div>
                <input
                  type="text"
                  value={dtmfBuffer}
                  onChange={(e) => setDtmfBuffer(e.target.value.replace(/[^0-9*#wW]/g, ''))}
                  placeholder="e.g. 1234#"
                  className="w-full border rounded-md text-sm font-mono px-2 py-1.5"
                  data-testid="dtmf-input"
                />
                <div className="grid grid-cols-3 gap-1">
                  {['1','2','3','4','5','6','7','8','9','*','0','#'].map(k => (
                    <button key={k} type="button" onClick={() => setDtmfBuffer(b => b + k)}
                      className="border rounded-md py-1 text-sm font-mono hover:bg-slate-100"
                      data-testid={`dtmf-key-${k}`}>{k}</button>
                  ))}
                </div>
                <button type="button" onClick={doSendDtmf} disabled={!dtmfBuffer}
                  className="w-full text-sm font-semibold bg-blue-600 text-white rounded-md py-1.5 hover:bg-blue-700 disabled:opacity-40"
                  data-testid="dtmf-send">
                  Send {dtmfBuffer && `"${dtmfBuffer}"`}
                </button>
              </div>
            )}

            {/* v3.21-1620 · Notes panel removed here.  Notes button now
                opens the STANDARD Client Workspace, which owns the
                authoritative Notes surface (server-side draft autosave
                + canonical /add-note Save Note flow).  A second local
                textarea would violate the "one Notes experience" rule. */}

            {/* Disposition sub-panel */}
            {panel === 'disposition' && (
              <div className="border-t pt-3 space-y-2" data-testid="disposition-panel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Call Disposition (required)</div>
                <select
                  value={dispCode}
                  onChange={(e) => setDispCode(e.target.value)}
                  className="w-full border rounded-md text-sm px-2 py-1.5"
                  data-testid="disposition-code"
                >
                  <option value="">— Choose —</option>
                  {DISPOSITIONS.map(([code, label]) => (
                    <option key={code} value={code}>{label}</option>
                  ))}
                </select>
                {dispCode === 'call_back' && (
                  <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                    Saving as Call Back will open the Callback panel so you can schedule the return.
                  </div>
                )}
                <button type="button" onClick={doSaveDisposition} disabled={!dispCode}
                  className="w-full text-sm font-semibold bg-emerald-600 text-white rounded-md py-1.5 hover:bg-emerald-700 disabled:opacity-40"
                  data-testid="disposition-save">
                  Save Disposition
                </button>
              </div>
            )}

            {/* Transfer sub-panel (warm | blind) */}
            {panel === 'transfer' && (
              <div className="border-t pt-3 space-y-2" data-testid="transfer-panel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
                  <ArrowLeftRight className="h-3 w-3" /> Transfer Call
                </div>
                <div className="flex gap-1.5">
                  <button type="button" onClick={() => setXferKind('warm')}
                    className={`flex-1 text-xs font-semibold py-1 rounded ${xferKind==='warm'?'bg-blue-600 text-white':'bg-slate-200 text-slate-700'}`}
                    data-testid="transfer-kind-warm">Warm</button>
                  <button type="button" onClick={() => setXferKind('blind')}
                    className={`flex-1 text-xs font-semibold py-1 rounded ${xferKind==='blind'?'bg-blue-600 text-white':'bg-slate-200 text-slate-700'}`}
                    data-testid="transfer-kind-blind">Blind</button>
                </div>
                {xferKind==='warm' && !ctrl.on_hold && (
                  <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Place the caller on Hold first, then start the warm transfer.
                  </div>
                )}
                <input type="tel" placeholder="+17578613626" value={xferTo}
                  onChange={(e) => setXferTo(e.target.value)}
                  className="w-full border rounded-md text-sm font-mono px-2 py-1.5"
                  data-testid="transfer-to" />
                <button type="button" onClick={doStartTransfer} disabled={!xferTo}
                  className="w-full text-sm font-semibold bg-blue-600 text-white rounded-md py-1.5 hover:bg-blue-700 disabled:opacity-40"
                  data-testid="transfer-start">
                  {xferKind==='blind' ? 'Transfer & Drop' : 'Start Warm Transfer'}
                </button>
              </div>
            )}

            {/* Add Participant sub-panel */}
            {panel === 'participant' && (
              <div className="border-t pt-3 space-y-2" data-testid="add-participant-panel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
                  <UserPlus className="h-3 w-3" /> Add Participant to Conference
                </div>
                <input type="tel" placeholder="+17578613626" value={addPartTo}
                  onChange={(e) => setAddPartTo(e.target.value)}
                  className="w-full border rounded-md text-sm font-mono px-2 py-1.5"
                  data-testid="add-participant-to" />
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={addPartMuted} onChange={e => setAddPartMuted(e.target.checked)}
                    data-testid="add-participant-muted" />
                  Join muted (listen-only)
                </label>
                <button type="button" onClick={doAddParticipant} disabled={!addPartTo}
                  className="w-full text-sm font-semibold bg-blue-600 text-white rounded-md py-1.5 hover:bg-blue-700 disabled:opacity-40"
                  data-testid="add-participant-submit">
                  Dial & Add to Conference
                </button>
              </div>
            )}

            {/* Callback sub-panel */}
            {panel === 'callback' && (
              <div className="border-t pt-3 space-y-2" data-testid="callback-panel">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
                  <Clock className="h-3 w-3" /> Schedule Callback
                </div>
                <input type="datetime-local" value={cbWhen}
                  onChange={(e) => setCbWhen(e.target.value)}
                  className="w-full border rounded-md text-sm px-2 py-1.5"
                  data-testid="callback-when" />
                <textarea value={cbNote} onChange={e => setCbNote(e.target.value)}
                  rows={2} placeholder="Optional note (why callback needed)…"
                  className="w-full border rounded-md text-sm px-2 py-1.5"
                  data-testid="callback-note" />
                <button type="button" onClick={doCallback} disabled={!cbWhen}
                  className="w-full text-sm font-semibold bg-blue-600 text-white rounded-md py-1.5 hover:bg-blue-700 disabled:opacity-40"
                  data-testid="callback-submit">
                  Schedule Callback Task
                </button>
              </div>
            )}

            {/* Footer — v3.21-1617 · End Call during telephony;
                Complete Call during Wrap-Up.  End Call disconnects
                the leg and transitions to Wrap-Up; only Complete Call
                closes the workspace. */}
            <div className="pt-2 border-t">
              {isWrapUp ? (
                <button
                  type="button"
                  onClick={doComplete}
                  disabled={completing}
                  className={[
                    'w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm font-semibold transition-colors',
                    ctrl.disposition && ((ctrl.notes_count||0) > 0 || noteText.trim()) && !completing
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      : 'bg-slate-200 text-slate-500 cursor-not-allowed',
                  ].join(' ')}
                  data-testid="active-call-complete"
                  title={
                    !ctrl.disposition ? 'Set a disposition before completing' :
                    ((ctrl.notes_count||0) === 0 && !noteText.trim()) ? 'Add at least one note before completing' :
                    'Complete the call and close this workspace'
                  }
                >
                  <ClipboardCheck className="h-4 w-4" /> Complete Call
                  {!ctrl.disposition && <span className="text-[10px] font-normal opacity-80">(pick disposition)</span>}
                  {ctrl.disposition && (ctrl.notes_count||0) === 0 && !noteText.trim() &&
                    <span className="text-[10px] font-normal opacity-80">(add a note)</span>}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={doEnd}
                  disabled={ending}
                  className={[
                    'w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm font-semibold transition-colors',
                    !ending
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-red-400 text-white cursor-not-allowed',
                  ].join(' ')}
                  data-testid="active-call-end"
                  title="End the live call now. Notes and disposition can be completed during Wrap-Up."
                >
                  <PhoneOff className="h-4 w-4" /> End Call
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <TransferCallModal
        isOpen={transferOpen}
        onClose={() => setTransferOpen(false)}
        activeCall={active}
        onTransferred={() => setTimeout(fetchActive, 1500)}
      />

      {/* v3.21-1620 · The Onboarding Call workspace has been
          superseded by the STANDARD Client Workspace, which is
          globally mounted in DashboardLayout via
          <ClientWorkspaceModal /> and driven by ClientWorkspaceContext.
          The Notes / Onboarding / Header buttons above open that
          workspace via `openClientWorkspace(clientId, {callSid})`. */}
      {/* v3.21-1612 · Reused Call Assist + Email composer. */}
      <CallAssistPanel
        open={callAssistOpen}
        onClose={() => setCallAssistOpen(false)}
        clientId={active?.client_id}
      />
      <EmailComposeDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        clientId={active?.client_id}
        clientName={active?.client_name}
      />
    </>
  );
}
